import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import InternalSidebar from '../../components/InternalSidebar';
import InternalTopMenu from '../../components/InternalTopMenu';
import EmailVerificationBanner from '../../components/EmailVerificationBanner';
import {
  ACCOUNT_LEVELS,
  resolveUserLevel,
  getRegistrationStep,
  getNextPendingLevel,
  MAX_ACCOUNT_LEVEL,
} from '../../constants/accountLevels';

// O que o cartão do nível 2 mostra quando a conta não pode enviar dados agora. O código do motivo
// vem do backend, junto da regra que decide o bloqueio, para a tela não repetir a regra.
const MOTIVOS_DO_BLOQUEIO = {
  email_not_verified: 'Confirme seu e-mail',
  attempts_exhausted: 'Fale com o suporte',
};

const AccountLevel = () => {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  // A situação do nível 2 vem do backend: é ela que diz se a esteira está operante neste ambiente e
  // em que pé está o último envio da conta. Enquanto não chega, o cartão não promete nada.
  const [situacaoKyc, setSituacaoKyc] = useState(null);
  const [carregandoKyc, setCarregandoKyc] = useState(true);

  const registrationStep = useMemo(() => getRegistrationStep(user), [user]);
  const nextPendingLevel = useMemo(() => getNextPendingLevel(user), [user]);
  const currentLevel = useMemo(() => resolveUserLevel(user), [user]);

  useEffect(() => {
    let ativo = true;

    api
      .get('/user/kyc/level2/status')
      .then((resposta) => {
        if (ativo) setSituacaoKyc(resposta.data);
      })
      .catch(() => {
        // Falha na consulta mantém o nível 2 fechado: é melhor não oferecer do que oferecer um
        // formulário que a rota vai recusar.
        if (ativo) setSituacaoKyc(null);
      })
      .finally(() => {
        if (ativo) setCarregandoKyc(false);
      });

    return () => {
      ativo = false;
    };
  }, []);

  // Quando a esteira aprova, quem está com a página aberta continuaria vendo o nível antigo até o
  // próximo login, porque o usuário fica guardado no navegador. A situação do backend é a fonte, e
  // atualizar o que está guardado é o que faz a tela mudar sem novo login.
  useEffect(() => {
    if (situacaoKyc?.status !== 'approved' || registrationStep >= 2) return;

    const atualizado = { ...user, userRegistrationStep: 2, underReview: 0 };
    localStorage.setItem('user', JSON.stringify(atualizado));
    setUser(atualizado);
  }, [situacaoKyc, registrationStep, user, setUser]);

  const handleNavigateToLevel = () => {
    if (nextPendingLevel !== null) {
      navigate(`/account/level/${nextPendingLevel}`);
    } else {
      navigate(`/account/level/${MAX_ACCOUNT_LEVEL}`);
    }
  };

  const botaoDesabilitado = (texto) => (
    <button
      type="button"
      className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-200 text-slate-400 px-4 py-2 text-xs font-semibold cursor-not-allowed"
      disabled
    >
      {texto}
    </button>
  );

  // O cartão do nível 2 é desenhado pelo que o backend responde, e não por uma condição escrita aqui:
  // no ambiente em que a base cadastral não está configurada, todo envio cairia na fila do analista, e
  // oferecer o nível 2 seria prometer uma verificação que ninguém faria na hora.
  const acaoDoNivelDois = () => {
    if (carregandoKyc) {
      return botaoDesabilitado('Carregando...');
    }

    if (!situacaoKyc?.automaticCheckAvailable) {
      return botaoDesabilitado('Em breve');
    }

    if (situacaoKyc.status === 'approved') {
      return (
        <span className="inline-flex items-center justify-center rounded-full bg-emerald-50 px-4 py-2 text-xs font-semibold text-emerald-600">
          Verificação aprovada
        </span>
      );
    }

    if (situacaoKyc.status === 'under_review') {
      return (
        <span className="inline-flex items-center justify-center rounded-full bg-amber-50 px-4 py-2 text-xs font-semibold text-amber-700">
          Em análise
        </span>
      );
    }

    // Recusado não libera reenvio: quem tentasse de novo até acertar nome ou nascimento viraria
    // ferramenta de teste de CPF alheio. Daqui em diante é o suporte quem resolve, pelo e-mail que
    // já foi enviado — não há botão de ação aqui, só o motivo.
    if (situacaoKyc.status === 'rejected') {
      return (
        <div className="space-y-2">
          {situacaoKyc.reviewNotes ? (
            <p className="text-xs text-red-600">
              Verificação não aprovada: {situacaoKyc.reviewNotes}
            </p>
          ) : null}
          {botaoDesabilitado('Fale com o suporte')}
        </div>
      );
    }

    if (!situacaoKyc.canSubmit) {
      return botaoDesabilitado(MOTIVOS_DO_BLOQUEIO[situacaoKyc.blockedReason] || 'Em breve');
    }

    return (
      <button
        type="button"
        className="inline-flex items-center justify-center gap-2 rounded-full bg-tBlue px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-tBlue2"
        onClick={() => navigate('/account/level/2')}
      >
        Completar este nível
        <i className="bi-arrow-right-short text-lg"></i>
      </button>
    );
  };

  const renderAction = (levelId) => {
    if (currentLevel === levelId) {
      if (nextPendingLevel === null) {
        return (
          <span className="inline-flex items-center justify-center rounded-full bg-slate-100 px-4 py-2 text-xs font-semibold text-slate-600">
            Você está neste nível
          </span>
        );
      }

      if (levelId === nextPendingLevel) {
        if (levelId === 2) {
          return acaoDoNivelDois();
        }
        return (
          <button
            type="button"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-tBlue px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-tBlue2"
            onClick={handleNavigateToLevel}
          >
            Completar este nível
            <i className="bi-arrow-right-short text-lg"></i>
          </button>
        );
      }

      return (
        <span className="inline-flex items-center justify-center rounded-full bg-slate-100 px-4 py-2 text-xs font-semibold text-slate-600">
          Você está neste nível
        </span>
      );
    }

    if (currentLevel > levelId) {
      return (
        <span className="inline-flex items-center justify-center rounded-full bg-emerald-50 px-4 py-2 text-xs font-semibold text-emerald-600">
          Nível concluído
        </span>
      );
    }

    if (nextPendingLevel === null) {
      return (
        <button
          type="button"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-slate-800"
          onClick={() => navigate(`/account/level/${MAX_ACCOUNT_LEVEL}`)}
        >
          Ver detalhes
          <i className="bi-arrow-right-short text-lg"></i>
        </button>
      );
    }

    const isNext = levelId === nextPendingLevel;
    const label = isNext ? 'Completar este nível' : 'Ver próximo nível';

    if (isNext && levelId === 2) {
      return acaoDoNivelDois();
    }

    return (
      <button
        type="button"
        className={`inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
          isNext ? 'bg-tBlue text-white hover:bg-tBlue2' : 'bg-slate-900 text-white hover:bg-slate-800'
        }`}
        onClick={handleNavigateToLevel}
      >
        {label}
        <i className="bi-arrow-right-short text-lg"></i>
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-[#f7f7f7] text-slate-700">
      <div className="min-h-screen flex flex-col lg:flex-row">
        <div className="lg:hidden">
          <div className="flex items-center justify-between px-6 py-4 bg-white shadow-sm">
            <button
              type="button"
              className="text-2xl text-slate-700"
              onClick={toggleSidebar}
              aria-label="Abrir menu"
            >
              <i className="bi-list"></i>
            </button>
            <img src="/img/branded/logob.png" alt="Stackfy" className="h-8" />
            <div className="w-7" />
          </div>
        </div>

        <InternalSidebar active="account-section" open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        {sidebarOpen && (
          <button
            type="button"
            className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="Fechar menu"
          ></button>
        )}

        <main className="flex-1 px-6 py-8 lg:px-10 lg:py-12">
          <EmailVerificationBanner />

          <header className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <button
              type="button"
              onClick={() => navigate('/account')}
              className="text-sm font-semibold flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            >
              <i className="bi-arrow-left-short text-xl"></i> Voltar
            </button>
            <InternalTopMenu onLogout={logout} />
          </header>

          <section className="max-w-5xl mx-auto space-y-8">
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">Nível da Conta</h1>
              <p className="mt-3 text-sm text-slate-500">
                Veja os níveis de verificação disponíveis, entenda os requisitos e saiba como
                liberar limites maiores para operar na Stackfy.
              </p>
            </div>

            <div className="grid gap-6 lg:grid-cols-3">
              {ACCOUNT_LEVELS.map((level) => (
                <article
                  key={level.id}
                  className={`relative rounded-3xl border px-6 py-6 flex flex-col gap-4 transition-shadow ${
                    currentLevel === level.id
                      ? 'border-tBlue shadow-lg bg-white ring-2 ring-tBlue/20'
                      : 'border-slate-200 bg-white shadow-sm'
                  }`}
                >
                  {currentLevel === level.id ? (
                    <span className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-tBlue px-3 py-1 text-[11px] font-semibold text-white shadow">
                      <i className="bi-star-fill text-xs"></i> Nível atual
                    </span>
                  ) : null}

                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">{level.title}</h2>
                    <p className="mt-2 text-xs text-slate-500">{level.description}</p>
                  </div>

                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                      Requisitos
                    </h3>
                    <ul className="mt-2 space-y-2 text-xs text-slate-600">
                      {level.requirements.map((item, index) => (
                        <li key={index} className="flex items-start gap-2">
                          {level.id <= currentLevel ? (
                            <i className="bi-check-circle-fill text-emerald-500 mt-0.5"></i>
                          ) : (
                            <i className="bi-circle text-slate-300 mt-0.5"></i>
                          )}
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                      Benefícios e limites
                    </h3>
                    <ul className="mt-2 space-y-2 text-xs text-slate-600">
                      {level.limits.map((item, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <i className="bi-lightning-charge-fill text-tBlue mt-0.5"></i>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mt-auto">{renderAction(level.id)}</div>
                </article>
              ))}
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white px-6 py-6 shadow-sm">
              <h2 className="text-base font-semibold text-slate-900 mb-3">Como avançar de nível?</h2>
              <ol className="list-decimal pl-5 space-y-2 text-xs text-slate-600">
                <li>Acesse a página Minha Conta {'>'} Nível da Conta para verificar o status atual do seu cadastro.</li>
                <li>Informe os dados necessários para o próximo nível e aguarde validação antes de prosseguir.</li>
                <li>
                  Assim que o nível for atualizado, os novos limites serão aplicados automaticamente
                  às suas operações.
                </li>
              </ol>
              <p className="mt-3 text-xs text-slate-500">
                Precisa de ajuda? Fale com nosso suporte{' '}
                <a
                  href="https://link.stackfy.app/websupport"
                  target="_blank"
                  rel="noreferrer"
                  className="text-tBlue font-semibold hover:text-tBlue2"
                >
                  StackHelp
                </a>
                .
              </p>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default AccountLevel;

